import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { HeroBackgroundStrokes } from '../components/common/SyncratikLogo';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        countryCode: '+1',
        message: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

    // Initialize EmailJS
    useEffect(() => {
        emailjs.init("q462Dgn0_4gwwzs1D");
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setSubmitStatus(null);

        try {
            // EmailJS template parameters
            const templateParams = {
                from_name: formData.fullName,
                from_email: formData.email,
                phone: `${formData.countryCode} ${formData.phoneNumber}`,
                to_email: 'info@syncratik.com',
                message: formData.message,
            };

            // Send email using EmailJS
            const response = await emailjs.send(
                'service_zneitc5',
                'template_c3386pm',
                templateParams
            );

            if (response.status === 200) {
                setSubmitStatus('success');
                setFormData({ fullName: '', email: '', phoneNumber: '', countryCode: '+1', message: '' });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Email sending failed:', error);
            setSubmitStatus('error');
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="bg-gray-10 min-h-screen relative pt-16 lg:pt-60 pb-60">
            {/* Background with logo strokes - same as homepage */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <HeroBackgroundStrokes
                    className="absolute inset-0 w-full"
                    style={{
                        height: '100%',
                        backgroundImage: 'url(/assets/logo-strokes.png)',
                        backgroundRepeat: 'repeat-y',
                        backgroundSize: '100% 2216px',
                        backgroundPosition: 'center top'
                    }}
                />
            </div>

            {/* Contact Form Section */}
            <section className="relative flex items-center justify-center">
                <div className="container-content relative z-10">
                    {/* Heading */}
                    <div className="text-center mb-16">
                        <h1 className="text-h1 md:text-h0 text-gray-95 mb-2">Talk to us</h1>
                        <p className="text-subheading text-gray-90">Our team can provide support for your sales, product, or other inquiries.</p>
                    </div>

                    {/* Form Card */}
                    <div className="max-w-xl mx-auto bg-gray-95 rounded-lg shadow-lg" style={{ padding: '1.75rem' }}>
                        {/* Success/Error Messages */}
                        {submitStatus === 'success' && (
                            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                                <p className="font-semibold">Message Sent Successfully!</p>
                                <p className="text-small">Thank you for your message. We will get back to you shortly.</p>
                            </div>
                        )}

                        {submitStatus === 'error' && (
                            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                                <p className="font-semibold">Failed to Send Message</p>
                                <p className="text-small">There was an error sending your message. Please try again or contact us directly at info@syncratik.com</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-7">
                            {/* Full Name */}
                            <div>
                                <label htmlFor="fullName" className="block text-paragraph font-medium text-gray-10 mb-2">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    placeholder="John Doe"
                                    required
                                    className="w-full px-4 py-3 rounded-md text-paragraph text-gray-10 placeholder-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-40 transition-all"
                                    style={{
                                        border: '1px solid transparent',
                                        backgroundImage: 'linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(to bottom, #d9d9d9, #b3b3b3)',
                                        backgroundOrigin: 'border-box',
                                        backgroundClip: 'padding-box, border-box'
                                    }}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-paragraph font-medium text-gray-10 mb-2">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="john@syncratik.com"
                                    required
                                    className="w-full px-4 py-3 rounded-md text-paragraph text-gray-10 placeholder-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-40 transition-all"
                                    style={{
                                        border: '1px solid transparent',
                                        backgroundImage: 'linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(to bottom, #d9d9d9, #b3b3b3)',
                                        backgroundOrigin: 'border-box',
                                        backgroundClip: 'padding-box, border-box'
                                    }}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Phone Number */}
                            <div>
                                <label htmlFor="phoneNumber" className="block text-paragraph font-medium text-gray-10 mb-2">
                                    Phone Number
                                </label>
                                <div
                                    className="flex rounded-md overflow-hidden"
                                    style={{
                                        border: '1px solid transparent',
                                        backgroundImage: 'linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(to bottom, #d9d9d9, #b3b3b3)',
                                        backgroundOrigin: 'border-box',
                                        backgroundClip: 'padding-box, border-box'
                                    }}
                                >
                                    <select
                                        name="countryCode"
                                        value={formData.countryCode}
                                        onChange={handleChange}
                                        className="pl-4 pr-1 py-3 text-paragraph text-gray-10 focus:outline-none focus:ring-0 transition-all border-0 bg-transparent"
                                        disabled={isLoading}
                                    >
                                        <option value="+1">+1</option>
                                        <option value="+44">+44</option>
                                        <option value="+91">+91</option>
                                        <option value="+86">+86</option>
                                        <option value="+81">+81</option>
                                        <option value="+49">+49</option>
                                        <option value="+33">+33</option>
                                        <option value="+61">+61</option>
                                        <option value="+977">+977</option>
                                    </select>
                                    <div
                                        className="ml-3 w-px"
                                        style={{
                                            backgroundImage: 'linear-gradient(to bottom, #d9d9d9, #b3b3b3)'
                                        }}
                                    ></div>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="(213) 695-0908"
                                        className="flex-1 pl-3 pr-4 py-3 text-paragraph text-gray-10 placeholder-gray-50 focus:outline-none focus:ring-0 transition-all border-0 bg-transparent"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Message */}
                            <div>
                                <label htmlFor="message" className="block text-paragraph font-medium text-gray-10 mb-2">
                                    How may we assist you?
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-md text-paragraph text-gray-10 placeholder-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-40 transition-all resize-none"
                                    style={{
                                        border: '1px solid transparent',
                                        backgroundImage: 'linear-gradient(#f5f5f5, #f5f5f5), linear-gradient(to bottom, #d9d9d9, #b3b3b3)',
                                        backgroundOrigin: 'border-box',
                                        backgroundClip: 'padding-box, border-box'
                                    }}
                                    disabled={isLoading}
                                ></textarea>
                            </div>

                            {/* Submit Button */}
                            <div>
                                <button
                                    type="submit"
                                    className={`btn-primary w-full ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    style={{ padding: '0.5rem 1rem' }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sending...
                                        </span>
                                    ) : (
                                        'Submit'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;

